import * as React from "react";

import ReactSelect, {
    GroupBase,
    Props,
    SelectInstance,
    StylesConfig,
} from "react-select";
import ChevronDownIcon from "../../icons/ChevronDown";
import { cn } from "../../../helpers";
import styles from "./Select.module.scss";

export function DropdownIndicator(canClear: boolean) {
    return (
        <ChevronDownIcon
            className={cn(styles.chevronIcon, { [styles.canClear]: canClear })}
        />
    );
}

export interface SelectProps extends Props {
    /** Select label text */
    label?: React.ReactNode;
    /** Check select has error */
    hasError?: boolean;
    /** what is the input size in relation to its height */
    inputSize?: "small";
    /** Select error text */
    errorText?: string;
    /** Select error text class for styling */
    errorTextClassName?: string;
    /** Id to link select and its label */
    id?: string;
    /** Styles object to edit styles outside of component */
    selectStyles?: StylesConfig<unknown, boolean, GroupBase<unknown>>;
    /** Class to style select outer container */
    wrapperClass?: string;
    /** Determines if the select isSearchable */
    isSearchable?: boolean;
    /** Function that is used to alter the label of the options */
    getOptionLabel?: (options: unknown) => string;
    /** Is field required, this will display "*" beside the label" */
    isRequired?: boolean;
    /** Mark field as optional, this will display "(optional)" beside the label" */
    isOptional?: boolean;
    /** Check if multiselect has values */
    multiHasValues?: boolean;
    /** Use this for long lists in dialogs */
    isLongListInDialog?: boolean;
    /** Menulist max height */
    menuListMaxHeight?: string;
}

export type SelectElement = SelectInstance<
    unknown,
    boolean,
    GroupBase<unknown>
> | null;

const Select = React.forwardRef<SelectElement, SelectProps>(
    (
        {
            label,
            hasError,
            inputSize,
            errorText,
            className,
            id,
            selectStyles,
            wrapperClass,
            isSearchable,
            getOptionLabel,
            isRequired,
            multiHasValues,
            isLongListInDialog,
            menuListMaxHeight,
            isOptional,
            errorTextClassName,
            ...props
        },
        ref
    ) => {
        if (label) {
            return (
                <div className={cn(styles.selectWrapper, wrapperClass)}>
                    <label htmlFor={id} className={styles.selectLabel}>
                        {label}
                        {isOptional ? (
                            <span
                                className={styles.optionalText}
                                aria-label="optional"
                            >
                                (optional)
                            </span>
                        ) : null}
                        {isRequired ? (
                            <span
                                className="text-danger text-sm"
                                aria-label="required"
                            >
                                *
                            </span>
                        ) : null}
                    </label>
                    <ReactSelect
                        getOptionLabel={getOptionLabel}
                        ref={ref}
                        aria-errormessage={errorText}
                        styles={{
                            container: () => ({
                                boxSizing: "border-box",
                            }),
                            indicatorSeparator: () => ({
                                display: "none",
                            }),
                            control: (_, { isDisabled, isFocused }) => ({
                                fontFamily: "inherit",
                                fontSize: "var(--text-sm)",
                                lineHeight: "22px",
                                position: "relative",
                                letterSpacing: "0.02em",
                                height: multiHasValues ? "auto" : "40px",
                                minHeight: multiHasValues ? "40px" : "auto",
                                padding: "0 12px",
                                background: hasError
                                    ? "rgba(247, 23, 53, 0.04)"
                                    : "white",
                                border:
                                    (hasError && "1px solid var(--color-danger)") ||
                                    (isFocused &&
                                        "1px solid var(--color-primary)") ||
                                    "1px solid var(--color-strokedark)",
                                borderRadius: "8px",
                                boxShadow: isFocused
                                    ? "0 0 0 3px var(--color-focus-ring)"
                                    : "none",
                                cursor: isDisabled ? "not-allowed" : "default",
                                opacity: isDisabled ? "0.8" : "1",
                                color: "var(--color-text-strong)",

                                "&:focus-visible": {
                                    border: "1px solid var(--color-primary)",
                                },
                            }),
                            valueContainer: () => ({
                                height: "100%",
                                minHeight: multiHasValues ? "40px" : undefined,
                                padding: multiHasValues ? "4px 0" : 0,
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "4px",
                            }),
                            noOptionsMessage: () => ({
                                color: "var(--color-gray)",
                                fontSize: "var(--text-sm)",
                                fontWeight: "500",
                                textAlign: "center",
                                fontFamily: "inherit",
                            }),
                            singleValue: () => ({
                                margin: 0,
                                fontSize: "var(--text-sm)",
                                lineHeight: "22px",
                                letterSpacing: "0.02em",
                                textTransform: "capitalize",
                                opacity: props.isDisabled ? 0.5 : 1,
                                "@media (min-width: 1024px)": {
                                    fontSize: "var(--text-sm)",
                                    lineHeight: "16px",
                                },
                            }),
                            placeholder: () => ({
                                margin: 0,
                                color: props.isDisabled
                                    ? "rgba(87, 106, 129, 0.8)"
                                    : "var(--color-gray)",
                                opacity: 0.9,
                                fontSize: "var(--text-sm)",
                                letterSpacing: "0.02em",
                            }),
                            menu: () => ({
                                background: "white",
                                boxShadow: "1px 4px 20px rgba(11, 19, 43, 0.08)",
                                borderRadius: "8px",
                                marginTop: "8px",
                                marginBottom: "8px",
                                position: "absolute",
                                width: "100%",
                                top: "100%",
                                zIndex: 10,
                                overflowY: "auto",
                            }),
                            menuList: () => ({
                                padding: "18px 8px",
                                maxHeight: isLongListInDialog
                                    ? menuListMaxHeight || "13rem"
                                    : menuListMaxHeight || "30rem",
                                overflowY: "auto",
                                position: "relative",
                            }),
                            option: (_, { isSelected }) => ({
                                color: "var(--color-gray)",
                                fontSize: "var(--text-sm)",
                                lineHeight: "19px",
                                fontWeight: "500",
                                padding: "6px 8px",
                                letterSpacing: "0.02em",
                                fontFamily: "inherit",
                                cursor: "pointer",
                                borderRadius: "4px",
                                backgroundColor: isSelected
                                    ? "var(--color-surface-hover)"
                                    : "transparent",

                                "&:hover": {
                                    backgroundColor: "rgba(185, 186, 163, 0.1)",
                                },

                                "&:not(:last-child)": {
                                    marginBottom: "0.6rem",
                                },
                            }),
                            clearIndicator: () => ({
                                display: "none",
                            }),
                            multiValue: () => ({
                                display: "inline-flex",
                                alignItems: "center",
                                minWidth: 0,
                                backgroundColor: "rgba(11, 122, 117, 0.15)",
                                borderRadius: "100px",
                                padding: "2px 8px",
                            }),
                            multiValueRemove: () => ({
                                padding: 0,
                                height: "auto",
                                cursor: "pointer",
                                display: props.isDisabled ? "none" : "block",
                            }),
                            multiValueLabel: () => ({
                                fontSize: "var(--text-sm)",
                            }),
                            indicatorsContainer: () => ({
                                display: props.isDisabled ? "none" : "block",
                            }),
                            ...selectStyles,
                        }}
                        className="h-full focus:border focus:border-transparent border-none"
                        placeholder="Select option"
                        menuPosition={isLongListInDialog ? "fixed" : "absolute"}
                        menuPortalTarget={
                            isLongListInDialog ? document.body : undefined
                        }
                        menuPlacement="auto"
                        id={id}
                        isSearchable={isSearchable || false}
                        components={{
                            DropdownIndicator: () =>
                                DropdownIndicator(Boolean(props.isClearable)),
                        }}
                        classNames={{
                            container: () =>
                                cn({ [styles.hasValues]: multiHasValues }),
                            clearIndicator: () => styles.clear,
                            multiValue: () => styles.multi_value,
                            menuPortal: () =>
                                cn({
                                    [styles.menuPortalDialog]: isLongListInDialog,
                                }),
                        }}
                        {...props}
                    />
                    {hasError && errorText ? (
                        <span className={styles.errorText}>{errorText}</span>
                    ) : null}
                </div>
            );
        }

        return (
            <div className={cn(styles.selectWrapper, wrapperClass)}>
                <ReactSelect
                    getOptionLabel={getOptionLabel}
                    ref={ref}
                    aria-errormessage={errorText}
                    styles={{
                        container: () => ({
                            boxSizing: "border-box",
                        }),
                        indicatorSeparator: () => ({
                            display: "none",
                        }),
                        control: (_, { isDisabled, isFocused }) => ({
                            fontFamily: "inherit",
                            fontSize: "var(--text-sm)",
                            lineHeight: "22px",
                            position: "relative",
                            letterSpacing: "0.02em",
                            height: multiHasValues ? "auto" : "40px",
                            minHeight: multiHasValues ? "40px" : "auto",
                            padding: "0 12px",
                            background: hasError
                                ? "var(--color-white)"
                                : "var(--color-white)",
                            border:
                                (hasError && "1px solid var(--color-danger)") ||
                                (isFocused && "1px solid var(--color-primary)") ||
                                "1px solid var(--color-strokedark)",
                            boxShadow: isFocused
                                ? "0 0 0 3px var(--color-focus-ring)"
                                : "none",
                            borderRadius: "8px",
                            cursor: isDisabled ? "not-allowed" : "default",
                            opacity: isDisabled ? "0.8" : "1",
                            color: "var(--color-text-strong)",
                            "&:focus-visible": {
                                border: "1px solid var(--color-primary)",
                            },
                        }),
                        valueContainer: () => ({
                            padding: 0,
                            height: "100%",
                            minHeight: multiHasValues ? "40px" : undefined,
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                        }),
                        noOptionsMessage: () => ({
                            color: "var(--color-gray)",
                            fontSize: "var(--text-sm)",
                            fontWeight: "500",
                            textAlign: "center",
                            fontFamily: "inherit",
                        }),
                        singleValue: () => ({
                            margin: 0,
                            fontSize: "var(--text-sm)",
                            lineHeight: "22px",
                            letterSpacing: "0.02em",
                            textTransform: "capitalize",
                            opacity: props.isDisabled ? 0.5 : 1,
                            "@media (min-width: 1024px)": {
                                fontSize: "var(--text-sm)",
                                lineHeight: "16px",
                            },
                        }),
                        placeholder: () => ({
                            margin: 0,
                            color: props.isDisabled
                                ? "rgba(87, 106, 129, 0.8)"
                                : "var(--color-gray)",
                            opacity: 0.9,
                            fontSize: "var(--text-sm)",
                            letterSpacing: "0.02em",
                        }),
                        menu: () => ({
                            background: "white",
                            boxShadow: "1px 4px 20px rgba(11, 19, 43, 0.08)",
                            borderRadius: "8px",
                            marginTop: "8px",
                            marginBottom: "8px",
                            position: "absolute",
                            width: "100%",
                            top: "100%",
                            zIndex: 10,
                            overflowY: "auto",
                        }),
                        menuList: () => ({
                            padding: "4px",
                            maxHeight: isLongListInDialog
                                ? menuListMaxHeight || "20rem"
                                : menuListMaxHeight || "30rem",
                            overflowY: "auto",
                            position: "relative",
                        }),
                        option: (_, { isSelected }) => ({
                            color: "var(--color-gray)",
                            fontSize: "var(--text-sm)",
                            lineHeight: "19px",
                            fontWeight: "500",
                            padding: "6px 16px",
                            letterSpacing: "0.02em",
                            fontFamily: "inherit",
                            cursor: "pointer",
                            borderRadius: "4px",
                            backgroundColor: isSelected
                                ? "var(--color-surface-hover)"
                                : "transparent",

                            "&:hover": {
                                backgroundColor: "rgba(185, 186, 163, 0.1)",
                            },

                            "&:not(:last-child)": {
                                marginBottom: "0.6rem",
                            },
                        }),
                        clearIndicator: () => ({
                            position: "absolute",
                            top: "50%",
                            right: "35px",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            "&:hover": {
                                backgroundColor: "var(--tone-danger-bg)",
                            },
                            "&:hover svg": {
                                fill: "var(--color-danger)",
                                stroke: "var(--color-danger)",
                            },
                            borderRadius: "9999px",
                        }),
                        multiValue: () => ({
                            display: "flex",
                            minWidth: 0,
                            backgroundColor: "var(--tone-neutral-bg)",
                            borderRadius: "2px",
                            marginLeft: "2px",
                        }),
                        multiValueRemove: () => ({
                            padding: 0,
                            height: "auto",
                            cursor: "pointer",
                            display: props.isDisabled ? "none" : "block",
                        }),
                        multiValueLabel: () => ({
                            fontSize: "var(--text-sm)",
                        }),
                        indicatorsContainer: () => ({
                            display: props.isDisabled ? "none" : "block",
                        }),
                        ...selectStyles,
                    }}
                    className="h-full focus:border focus:border-transparent border-none"
                    placeholder="Select option"
                    menuPosition={isLongListInDialog ? "fixed" : "absolute"}
                    menuPortalTarget={isLongListInDialog ? document.body : undefined}
                    menuPlacement="auto"
                    id={id}
                    isSearchable={isSearchable || false}
                    components={{
                        DropdownIndicator: () =>
                            DropdownIndicator(Boolean(props.isClearable)),
                    }}
                    classNames={{
                        container: () => cn({ [styles.hasValues]: multiHasValues }),
                        clearIndicator: () => styles.clear,
                        multiValue: () => styles.multi_value,
                        menuPortal: () =>
                            cn({
                                [styles.menuPortalDialog]: isLongListInDialog,
                            }),
                    }}
                    {...props}
                />

                {hasError && errorText ? (
                    <span className={styles.errorText}>{errorText}</span>
                ) : null}
            </div>
        );
    }
);

Select.displayName = "Select";

export { Select };
